/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    Channel as IChannel,
    SourceType,
    StationPolicy,
    VideoResolution,
} from 'client-code/channels-service';

import { CachingPolicy } from '../cachingpolicy/CachingPolicy';
import { DeviceFeatures } from '../device';
import { Channel } from '../entities/Channel';
import { Channels } from '../entities/Channels';
import { ChannelsServiceRepositoryImpl, ChannelsServiceRepositoryImplV2 } from '../impls';
import { IChannelsService } from '../interfaces';
import { Log } from '../logging';
import { Storage } from '../storage';
import { TChannels } from '../types/TChannels';
import { ChannelsType } from '../types/TChannelsType';
import { TServicesParams } from '../types/TServicesParams';
import { StationPolicyService } from './StationPolicyService';

type ChannelsIndexMapType = { [channelNumber: string]: number };

export class ChannelsService implements IChannelsService {
    private readonly TAG: string = 'ChannelsService';

    private readonly mParams: TServicesParams;

    private readonly oChannelsService:
        | ChannelsServiceRepositoryImpl
        | ChannelsServiceRepositoryImplV2;

    private static allChannels: Array<string> = [];

    private static allChannelsIndexMap: ChannelsIndexMapType = {};

    private static favoriteChannels: Array<string> = [];

    private static favoriteChannelsIndexMap: ChannelsIndexMapType = {};

    private static shouldUpdateIndexMapping: boolean = true;

    private static subscribedChannels: Array<string> = [];

    private static subscribedChannelsIndexMap: ChannelsIndexMapType = {};

    private static readonly stationPoliciesMap: Map<string, StationPolicy> = new Map();

    public static readonly ENDPOINT = {
        V1: 'v1',
        V2: 'v2',
    };

    public constructor(params: TServicesParams) {
        this.mParams = params;
        this.oChannelsService = this.createChannelService(params);
        this.updateArrayIndexMapping();
    }

    // eslint-disable-next-line class-methods-use-this
    private createChannelService(params: TServicesParams) {
        return params?.endpoint === ChannelsService.ENDPOINT.V2
            ? new ChannelsServiceRepositoryImplV2(params)
            : new ChannelsServiceRepositoryImpl(params);
    }

    // eslint-disable-next-line class-methods-use-this
    private clearAllStaticStorage() {
        ChannelsService.allChannelsIndexMap = {};
        ChannelsService.allChannels = [];
        ChannelsService.subscribedChannels = [];
        ChannelsService.subscribedChannelsIndexMap = {};
        ChannelsService.favoriteChannels = [];
        ChannelsService.favoriteChannelsIndexMap = {};
        ChannelsService.stationPoliciesMap.clear();
    }

    private async fetchAndStoreAllChannels(): Promise<void> {
        try {
            const [allChannels, subscribedChannels, favoriteChannels, stationPolicyData] =
                await Promise.all([
                    this.getChannelsFromServer(),
                    this.getChannelsFromServer(false, true),
                    this.getChannelsFromServer(true),
                    new StationPolicyService(this.mParams).fetchStationPolicies(),
                ]);

            if (!allChannels.channels.length) {
                allChannels.channels = this.getChannelsFromStore();
            }

            allChannels.channels.forEach((channel, _index) => {
                ChannelsService.allChannelsIndexMap[channel.channelNumber!] = _index;
            });

            if (subscribedChannels.channels.length) {
                subscribedChannels.channels.forEach((channel) => {
                    const channelIndex =
                        ChannelsService.allChannelsIndexMap[channel.channelNumber!];
                    const unsubscribedChannel = allChannels.channels[channelIndex];
                    unsubscribedChannel.isSubscribed = true;
                });
            }

            favoriteChannels.channels.forEach((channel) => {
                const channelIndex = ChannelsService.allChannelsIndexMap[channel.channelNumber!];
                const favoriteChannel = allChannels.channels[channelIndex];
                favoriteChannel.isFavorite = true;
            });

            Storage.getInstance().setItem(Storage.Keys.CHANNELS, allChannels.channels);
            Storage.getInstance().setItem(
                Storage.Keys.CHANNELS_SNAPSHOT_VERSION,
                allChannels.snapshotVersion,
            );

            if (stationPolicyData) {
                stationPolicyData.forEach((policy: StationPolicy) => {
                    ChannelsService.stationPoliciesMap.set(policy.name, policy);
                });
            }
        } catch (error) {
            Log.e(this.TAG, 'fetchAndStoreAllChannels() - cannot store channels data', error);
            throw error;
        }
    }

    private async getChannelsFromServer(
        isFavorite: boolean = false,
        isReceived: boolean = false,
    ): Promise<Channels> {
        const responseData = await this.oChannelsService.getChannelsFromServer(
            isFavorite,
            isReceived,
        );
        const filteredChannelsResponse = this.getFilteredChannels(responseData.channels);
        const channelsData = filteredChannelsResponse?.map(
            (channel) => new Channel(channel as IChannel, this.mParams.channelLogoBaseUrl),
        );

        return new Channels({
            ...responseData,
            channels: channelsData,
        });
    }

    private static getChannelsIndexMap(channelsType: string): ChannelsIndexMapType {
        return channelsType === ChannelsType.FAVORITE
            ? this.favoriteChannelsIndexMap
            : this.allChannelsIndexMap;
    }

    private getChannelsList(channelsType: string): Channel[] {
        const allChannels = this.getChannelsFromStore();

        return allChannels.filter((channel) =>
            channelsType === ChannelsType.FAVORITE ? channel.isFavorite : channel,
        );
    }

    private getChannelsFromStore(): Array<Channel> {
        Log.i(this.TAG, 'getChannelsFromStore', 'fetching channels from updated store');
        return Storage.getInstance().getItem(Storage.Keys.CHANNELS);
    }

    // eslint-disable-next-line no-unused-vars
    private getFilteredChannels(channels?: Channel[]): Channel[];

    // eslint-disable-next-line no-unused-vars
    private getFilteredChannels(channels?: IChannel[]): IChannel[];

    private getFilteredChannels(channels?: any): any {
        Log.i(this.TAG, 'filtering channels based on various flags');
        let filteredChannels = channels;
        filteredChannels = DeviceFeatures.shouldFilterJumpChannels()
            ? filteredChannels?.filter((channel) => channel.sourceType !== SourceType.JumpChannel)
            : filteredChannels;

        filteredChannels = DeviceFeatures.shouldFilterPpvChannels()
            ? filteredChannels?.filter((channel) => !channel.isPayPerView)
            : filteredChannels;

        filteredChannels = DeviceFeatures.shouldFilter4KChannels()
            ? filteredChannels?.filter((channel) => channel.videoResolution !== VideoResolution.Uhd)
            : filteredChannels;
        return filteredChannels;
    }

    // eslint-disable-next-line class-methods-use-this
    private getChannelsAndChannelsIndexMap(channelsType: string) {
        let channelsIndexMap;
        let channels;
        switch (channelsType) {
            case ChannelsType.ALL:
                channelsIndexMap = ChannelsService.allChannelsIndexMap;
                channels = ChannelsService.allChannels;

                break;
            case ChannelsType.FAVORITE: // favourites
                channelsIndexMap = ChannelsService.favoriteChannelsIndexMap;
                channels = ChannelsService.favoriteChannels;

                break;
            case ChannelsType.SUBSCRIBED: // subscribed
            default:
                channels = ChannelsService.subscribedChannels;
                channelsIndexMap = ChannelsService.subscribedChannelsIndexMap;
                break;
        }
        return { channels, channelsIndexMap };
    }

    private async getChannels(
        type: ChannelsType,
        offset?: number,
        limit?: number,
    ): Promise<TChannels> {
        try {
            if (limit && limit < 0) throw new Error('invalid value of limit');
            if (offset && offset < 0) throw new Error('invalid value of offset');

            await this.updateChannelsCache();
            const allChannels = this.getChannelsFromStore();
            const channels: Array<string> = ChannelsService[type];
            const channelCount = channels?.length;
            const start = !offset ? 0 : offset;
            let resultChannelsCount = channelCount;
            if (limit) {
                resultChannelsCount = Math.min(limit, channelCount);
            }
            const end = start + resultChannelsCount;
            let requiredChannels = channels?.slice(start, end);
            /**
             * logic to add channels from starting if the available channels from offset does not
             * fullfil the limit. it will append the remaining channels from the starting,
             * producing cyclic view in guide
             */
            if (end > channelCount) {
                requiredChannels = requiredChannels.concat(channels?.slice(0, end % channelCount));
            }

            const requiredChannelsData = requiredChannels?.map((channelNumber) => {
                const channelIndex = ChannelsService.allChannelsIndexMap[channelNumber];
                const requiredChannel = allChannels[channelIndex];
                requiredChannel.policyInfo = requiredChannel.policyName?.map((policy) => {
                    return ChannelsService.stationPoliciesMap.get(policy);
                });
                return requiredChannel;
            });

            return <TChannels>{
                channels: requiredChannelsData,
                channelCount,
                currentOffset: offset,
            };
        } catch (error) {
            Log.e(this.TAG, 'getChannels()', error);
            throw error;
        }
    }

    private updateArrayIndexMapping(): void {
        if (!ChannelsService.shouldUpdateIndexMapping) return;

        this.clearAllStaticStorage();
        let channels = this.getChannelsFromStore();
        channels = this.getFilteredChannels(channels);
        Storage.getInstance().setItem(Storage.Keys.CHANNELS, channels);

        let subscribedChannelsIndex = 0;
        let favoriteChannelsIndex = 0;

        if (channels) {
            channels.forEach((channel, _index) => {
                ChannelsService.allChannelsIndexMap[channel.channelNumber!] = _index;
                ChannelsService.allChannels.push(channel.channelNumber!);

                if (channel.isSubscribed) {
                    ChannelsService.subscribedChannels.push(channel.channelNumber!);
                    ChannelsService.subscribedChannelsIndexMap[channel.channelNumber!] =
                        subscribedChannelsIndex;
                    subscribedChannelsIndex += 1;
                }

                if (channel.isFavorite) {
                    ChannelsService.favoriteChannels.push(channel.channelNumber!);
                    ChannelsService.favoriteChannelsIndexMap[channel.channelNumber!] =
                        favoriteChannelsIndex;
                    favoriteChannelsIndex += 1;
                }
            });
        }
        const stationPolicyData = Storage.getInstance().getItem(Storage.Keys.STATION_POLICIES);
        if (stationPolicyData) {
            stationPolicyData.forEach((policy: StationPolicy) => {
                ChannelsService.stationPoliciesMap.set(policy.name, policy);
            });
        }
        ChannelsService.shouldUpdateIndexMapping = false;
    }

    private async updateChannelsCache(): Promise<void> {
        try {
            const currentTime = Date.now();
            const channelsDataExpiryTime = Storage.getInstance().getItem(
                Storage.Keys.CHANNELS_DATA_LOCAL_CACHE_EXPIRY_TIME,
            );
            const localCacheExpired: boolean =
                !channelsDataExpiryTime || channelsDataExpiryTime < currentTime;
            if (localCacheExpired) {
                await this.fetchAndStoreAllChannels();
                ChannelsService.shouldUpdateIndexMapping = true;
                this.updateArrayIndexMapping();
                Storage.getInstance().setItem(
                    Storage.Keys.CHANNELS_DATA_LOCAL_CACHE_EXPIRY_TIME,
                    currentTime + CachingPolicy.ChannelsService.cachingDuration,
                );
            }
        } catch (error) {
            Log.e(this.TAG, 'updateChannelsCache()', error);
            if (!Storage.getInstance().getItem(Storage.Keys.CHANNELS)) throw error;
        }
    }

    /**
     * Returns array of channels object for the given range,
     * if limit and offset is not passed, array of all channels object will be returned
     * @param offset {number} skip offset channels
     * @param limit {number} return limit channels
     * @returns {Promise<TChannels>} object of {channels,channelCount}
     */
    public async fetchAllChannels(offset?: number, limit?: number): Promise<TChannels> {
        return this.getChannels(ChannelsType.ALL, offset, limit);
    }

    /**
     * Returns array of favorite channels object for the given range,
     * if limit and offset is not passed, array of favorite channels object will be returned
     * @param offset {number} skip offset channels
     * @param limit {number} return limit channels
     * @returns {Promise<TChannels>} object of {channels,channelCount}
     */
    public async fetchFavoriteChannels(offset?: number, limit?: number): Promise<TChannels> {
        return this.getChannels(ChannelsType.FAVORITE, offset, limit);
    }

    /**
     * Returns array of subscribed channels object for the given range,
     * if limit and offset is not passed, array of all subscribed channels object will be returned
     * @param offset {number} skip offset channels
     * @param limit {number} return limit channels
     * @returns {Promise<TChannels>} object of {channels,channelCount}
     */
    public async fetchMyChannels(offset?: number, limit?: number): Promise<TChannels> {
        return this.getChannels(ChannelsType.SUBSCRIBED, offset, limit);
    }

    /**
     * Returns array of channels object from the supplied channelNumber as
     * accordance with the channelsType and limit.
     * @param {string} channelNumber
     * @param {ChannelsType} channelsType
     * @param {number} limit
     * @return {*}  {Promise<TChannels>}
     * @memberof ChannelsService
     */
    public async fetchNextChannelsFromChannelNumber(
        channelNumber: string,
        channelsType: ChannelsType,
        limit: number,
    ): Promise<TChannels> {
        try {
            await this.updateChannelsCache();

            let channelsIndexMap;
            switch (channelsType) {
                case ChannelsType.ALL:
                    channelsIndexMap = ChannelsService.allChannelsIndexMap;
                    break;
                case ChannelsType.FAVORITE:
                    channelsIndexMap = ChannelsService.favoriteChannelsIndexMap;
                    break;
                case ChannelsType.SUBSCRIBED:
                default:
                    channelsIndexMap = ChannelsService.subscribedChannelsIndexMap;
                    break;
            }

            const currentChannelIndex = channelsIndexMap[channelNumber];
            if (currentChannelIndex)
                return this.getChannels(channelsType, currentChannelIndex, limit);
            const channelNumbers = Object.keys(channelsIndexMap);
            const nextChannelNumberIndex = channelNumbers.findIndex(
                (number) => Number(number) > Number(channelNumber),
            );
            if (nextChannelNumberIndex <= 0)
                return this.getChannels(channelsType, channelNumbers.length - 1, limit);
            return this.getChannels(channelsType, nextChannelNumberIndex - 1, limit);
        } catch (error) {
            Log.e(this.TAG, 'fetchNextChannesl', error);
            throw error;
        }
    }

    /**
     * return next channel object from the currnt channel number
     * @param channelNumber {string} channel number
     * @returns {Channel} channel object of next channel
     */
    public async getNextChannel(
        channelNumber: string,
        channelsType: string,
        count: number = 1,
    ): Promise<Channel> {
        try {
            await this.updateChannelsCache();
            const { channels, channelsIndexMap } =
                this.getChannelsAndChannelsIndexMap(channelsType);
            const currentChannelIndex = channelsIndexMap[channelNumber] || 0;
            const allChannels = this.getChannelsList(channelsType);
            const nextChannelIndex =
                (currentChannelIndex + (count % channels.length)) % channels.length;
            const nextChannelNumber = channels[nextChannelIndex];
            const nextChannelIndexInAllChannels =
                ChannelsService.getChannelsIndexMap(channelsType)[nextChannelNumber];
            const nextRequiredChannel = allChannels[nextChannelIndexInAllChannels];
            nextRequiredChannel.policyInfo = nextRequiredChannel.policyName?.map((policy) =>
                ChannelsService.stationPoliciesMap.get(policy),
            );

            return nextRequiredChannel;
        } catch (error) {
            Log.e(this.TAG, 'getNextChannel', error);
            throw error;
        }
    }

    /**
     * return previous channel object from the currnt channel number
     * @param channelNumber {string} channel number
     * @returns {Channel} channel object of previous channel
     */
    public async getPreviousChannel(
        channelNumber: string,
        channelsType: string,
        count: number = 1,
    ): Promise<Channel> {
        try {
            const { channels, channelsIndexMap } =
                this.getChannelsAndChannelsIndexMap(channelsType);
            await this.updateChannelsCache();
            const allChannels = this.getChannelsList(channelsType);
            const currentChannelIndex = channelsIndexMap[channelNumber];
            const previousChannelIndex =
                (currentChannelIndex - (count % channels.length) + channels.length) %
                channels.length;

            const previousChannelNumber = channels[previousChannelIndex];
            const previousChannelIndexInAllChannels =
                ChannelsService.getChannelsIndexMap(channelsType)[previousChannelNumber];
            const previousRequiredChannel = allChannels[previousChannelIndexInAllChannels];
            previousRequiredChannel.policyInfo = previousRequiredChannel.policyName?.map(
                (policy) => {
                    return ChannelsService.stationPoliciesMap.get(policy);
                },
            );
            return previousRequiredChannel;
        } catch (error) {
            Log.e(this.TAG, 'getPreviousChannel', error);
            throw error;
        }
    }

    /**
     * return obect of requested channel bumber
     * @param channelNumber {string} requested channel number
     * @returns {Channel} channel object of requested channel
     */
    public async tuneToChannel(channelNumber: string): Promise<Channel> {
        try {
            const currentChannelIndex = ChannelsService.allChannelsIndexMap[channelNumber];
            if (currentChannelIndex === undefined)
                throw new Error(`cannot find channel with channel number: ${channelNumber}`);
            const channels = this.getChannelsFromStore();
            const requiredChannel = channels[currentChannelIndex];
            requiredChannel.policyInfo = requiredChannel.policyName?.map((policy) => {
                return ChannelsService.stationPoliciesMap.get(policy);
            });
            return requiredChannel;
        } catch (error) {
            Log.e(this.TAG, 'tuneToChannel', error);
            throw error;
        }
    }

    /**
     * Update and stores the channels object by adding the tuning URI.
     * @param {string} channelNumber
     * @param {string} url
     * @memberof ChannelsService
     */
    public updateChannelUrl(channelNumber: string, url: string): void {
        const currentChannelIndex = ChannelsService.allChannelsIndexMap[channelNumber];
        const allChannels = this.getChannelsFromStore();
        allChannels[currentChannelIndex].tuningUri = [url];
        Storage.getInstance().setItem(Storage.Keys.CHANNELS, allChannels);
    }

    /**
     * This method will be used to update the a channel to favorite or not in localStorage
     * We try to do optimistic updates.
     * @param channel
     * @param isFavorite
     */
    public async updateFavoriteChannel(channel: Channel, isFavorite: boolean): Promise<void> {
        const channels = this.getChannelsFromStore();
        channels.forEach((ch, index) => {
            if (ch.channelId === channel.channelId) {
                channels[index].isFavorite = isFavorite;
            }
        });
        Storage.getInstance().setItem(Storage.Keys.CHANNELS, channels);
        ChannelsService.shouldUpdateIndexMapping = true;
        this.updateArrayIndexMapping();
    }
}
